import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CampaignStatus } from 'src/app/models/campaign';
import * as moment from 'moment';
import { CampaignService } from 'src/app/services/campaign.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

@Component({
    selector: 'app-campaign-cancel-modal',
    templateUrl: './campaign-cancel-modal.component.html',
    styleUrls: ['./campaign-cancel-modal.component.scss']
})
export class CampaignCancelModalComponent implements OnInit {
    public step: number = 1;

    public campaignId: string;
    public startsInLessThan2WorkingDays: boolean;
    public status: CampaignStatus;
    public startDate: Date;
    public endDate: Date;
    public isLoaded: boolean = false;
    public today: Date = new Date();
    public minStartDate: Date;
    public upcomingStatusDate: Date | null = null;
    public isAdmin: boolean = false;
    public startsIn2WorkingDays: boolean = false;

    dateFilter = (d: Date | null): boolean => {
        const date = d || new Date();
        return date >= new Date();
    };

    constructor(
        public dialog: MatDialog,
        public dialogRef: MatDialogRef<CampaignCancelModalComponent>,
        private campaignService: CampaignService,
        private authenticationService: AuthenticationService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.campaignId = data.campaignId;
        this.status = data.status;
        this.startDate = data.startDate;
        this.endDate = data.endDate;
        this.minStartDate = moment(this.today).add(2, 'd').toDate();
        this.isAdmin = this.authenticationService.isAdmin();

        if (data.startsIn2WorkingDays !== undefined) {
            this.startsIn2WorkingDays = data.startsIn2WorkingDays;
        } else {
            this.startsIn2WorkingDays = false;
        }

        if (
            this.isAdmin &&
            (this.status === CampaignStatus.LIVE ||
                this.status === CampaignStatus.PARTIALLY_LIVE ||
                this.status === CampaignStatus.PARTIALLY_APPROVED ||
                this.status === CampaignStatus.PARTIALLY_REJECTED ||
                this.status === CampaignStatus.APPROVED)
        ) {
            this.step = 2;
            this.upcomingStatusDate = new Date();
        } else if (![CampaignStatus.LIVE, CampaignStatus.PARTIALLY_LIVE].includes(this.status)) {
            this.step = 0;
            this.checkIfStartsInLessThan2WorkingDays();
        }
    }

    ngOnInit(): void {
        this.checkIfStartsInLessThan2WorkingDays();
    }

    onUpcomingStatusDateChanged(date: Date | null): void {
        if (date) this.upcomingStatusDate = date;
    }

    isUpcomingDateWithinRange(): boolean {
        if (!this.upcomingStatusDate || !this.startDate || !this.endDate) {
            return false;
        }

        const upcoming = moment(this.upcomingStatusDate);
        const start = moment(this.today).add(1, 'd').toDate();
        const end = moment(this.endDate);

        const result = upcoming.isAfter(start) && upcoming.isSameOrBefore(end);
        return result;
    }

    moveToStepTwo() {
        this.step = 2;
    }

    getTitle() {
        if (
            this.isAdmin &&
            (this.status === CampaignStatus.PARTIALLY_APPROVED ||
                this.status === CampaignStatus.PARTIALLY_REJECTED ||
                this.status === CampaignStatus.APPROVED)
        ) {
            if (this.startsInLessThan2WorkingDays) {
                return 'The start date of the campaign is within 2 days, are you sure you want to force the cancellation?';
            } else {
                return 'Are you sure you want to cancel the campaign?';
            }
        }

        if (
            this.isAdmin &&
            (this.status === CampaignStatus.LIVE || this.status === CampaignStatus.PARTIALLY_LIVE)
        ) {
            return 'This campaign is live, are you sure you want to cancel it?';
        }

        // Default titles for non-admin users
        let title;

        if (
            [
                CampaignStatus.PENDING_APPROVAL,
                CampaignStatus.PENDING_REAPPROVAL,
                CampaignStatus.PARTIALLY_LIVE,
                CampaignStatus.LIVE,
                CampaignStatus.PAUSED,
                CampaignStatus.REJECTED,
                CampaignStatus.PARTIALLY_REJECTED
            ].includes(this.status)
        ) {
            title = 'Are you sure you want to cancel this campaign?';
        }

        if (
            [
                CampaignStatus.APPROVED,
                CampaignStatus.PARTIALLY_APPROVED,
                CampaignStatus.PAUSED_PENDING_SUP_APPROVALS
            ].includes(this.status) &&
            !this.startsInLessThan2WorkingDays
        ) {
            title = 'Are you sure you want to cancel this campaign?';
        }

        return title;
    }

    getCancelDescription() {
        if (
            this.isAdmin &&
            (this.status === CampaignStatus.LIVE ||
                this.status === CampaignStatus.PARTIALLY_LIVE ||
                this.status === CampaignStatus.PARTIALLY_APPROVED ||
                this.status === CampaignStatus.PARTIALLY_REJECTED ||
                this.status === CampaignStatus.APPROVED)
        ) {
            return '';
        }

        let description;

        if (
            [
                CampaignStatus.PENDING_APPROVAL,
                CampaignStatus.PENDING_REAPPROVAL,
                CampaignStatus.REJECTED
            ].includes(this.status)
        ) {
            description =
                'In case any supplier has already paid the municipality fees on your behalf, it will be charged separately. Are you sure you want to cancel?';
        }

        if (
            [
                CampaignStatus.APPROVED,
                CampaignStatus.PARTIALLY_APPROVED,
                CampaignStatus.PAUSED_PENDING_SUP_APPROVALS,
                CampaignStatus.PARTIALLY_REJECTED
            ].includes(this.status) &&
            this.startsInLessThan2WorkingDays
        ) {
            description =
                'Your campaign will start in less than 2 working days, please contact the media connector support if you would like to cancel.';
        }

        if (
            [
                CampaignStatus.APPROVED,
                CampaignStatus.PARTIALLY_APPROVED,
                CampaignStatus.PARTIALLY_REJECTED
            ].includes(this.status) &&
            !this.startsInLessThan2WorkingDays
        ) {
            description =
                'In case any supplier has already paid the municipality fees on your behalf, it will be charged separately. Are you sure you want to cancel?';
        }

        // For non-admin users with live campaigns
        if (!this.isAdmin && this.status === CampaignStatus.LIVE) {
            description =
                'Your campaign is currently live. Are you sure you want to cancel? Note: If you need to cancel it urgently, please contact the media connector support.';
        }

        if (!this.isAdmin && this.status === CampaignStatus.PARTIALLY_LIVE) {
            description =
                'Your campaign is currently partially live. Are you sure you want to cancel? Note: If you need to cancel it urgently, please contact the media connector support';
        }

        return description;
    }

    checkIfStartsInLessThan2WorkingDays() {
        this.campaignService.startsInLessThan2WorkingDays(this.campaignId).subscribe(result => {
            this.startsInLessThan2WorkingDays = result;
            this.isLoaded = true;
        });
    }

    canCancelCampaign(): boolean {
        if (
            this.isAdmin &&
            (this.status === CampaignStatus.LIVE ||
                this.status === CampaignStatus.PARTIALLY_LIVE ||
                this.status === CampaignStatus.PARTIALLY_APPROVED ||
                this.status === CampaignStatus.PARTIALLY_REJECTED ||
                this.status === CampaignStatus.APPROVED)
        ) {
            return true;
        }

        // Default logic for other users
        const canCancel = [
            CampaignStatus.PENDING_APPROVAL,
            CampaignStatus.PENDING_REAPPROVAL,
            CampaignStatus.REJECTED,
            CampaignStatus.PAUSED,
            CampaignStatus.LIVE,
            CampaignStatus.PARTIALLY_LIVE
        ].includes(this.status);

        const longerThan2WorkingDays =
            [
                CampaignStatus.APPROVED,
                CampaignStatus.PARTIALLY_APPROVED,
                CampaignStatus.PARTIALLY_REJECTED
            ].includes(this.status) && !this.startsInLessThan2WorkingDays;

        return canCancel || longerThan2WorkingDays;
    }

    cancelCampaign(): void {
        this.campaignService
            .updateCampaignStatus(
                this.campaignId,
                CampaignStatus.CANCELLED,
                this.upcomingStatusDate
            )
            .subscribe(_ => {
                this.dialogRef.close();
            });
    }
}
